import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_MathFunctions = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">🧮 CSS Math Functions</h1>

      <p className="tutorial-intro">
        CSS provides powerful <b>math functions</b> like <code>calc()</code>,{" "}
        <code>min()</code>, <code>max()</code>, and <code>clamp()</code> to
        create flexible and responsive designs. These functions allow you to
        perform calculations directly in CSS.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #f0fdf4, #bbf7d0)",
          padding: "1rem",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#065f46", marginBottom: "0.5rem" }}>
          🔑 Key Points
        </h2>
        <ul
          style={{
            lineHeight: "1.8",
            fontSize: "1.05rem",
            paddingLeft: "25px",
          }}
        >
          <li>
            <code>calc()</code> → lets you mix units (<code>px</code>,{" "}
            <code>%</code>, <code>em</code>, etc.) in calculations.
          </li>
          <li>
            <code>min()</code> → picks the <b>smallest value</b> from its
            arguments.
          </li>
          <li>
            <code>max()</code> → picks the <b>largest value</b> from its
            arguments.
          </li>
          <li>
            <code>clamp()</code> → sets a value that’s clamped between{" "}
            <b>min, preferred, and max</b>.
          </li>
          <li>
            Useful for <b>responsive typography, layouts, and spacing</b>.
          </li>
        </ul>
      </div>

      {/* Example 1: calc() */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 1: calc()</h2>
        <p>
          The <code>calc()</code> function lets you perform calculations to
          determine CSS property values. You can combine units like{" "}
          <code>px</code>, <code>%</code>, <code>em</code>, etc.
        </p>

        <CodeCopyBlock
          code={`.box {
  width: calc(100% - 50px);
  height: calc(200px + 2em);
}`}
          language="css"
        />

        <div
          style={{
            width: "calc(100% - 50px)",
            height: "80px",
            background: "#bfdbfe",
            marginTop: "1rem",
            textAlign: "center",
            lineHeight: "80px",
          }}
        >
          Width = 100% - 50px
        </div>
      </div>

      {/* Example 2: min() */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 2: min()</h2>
        <p>
          The <code>min()</code> function picks the <b>smallest value</b> among
          its arguments.
        </p>

        <CodeCopyBlock
          code={`.box {
  width: min(50%, 300px);
}`}
          language="css"
        />

        <div
          style={{
            width: "min(50%, 300px)",
            height: "80px",
            background: "#fde68a",
            marginTop: "1rem",
            textAlign: "center",
            lineHeight: "80px",
          }}
        >
          Width = min(50%, 300px)
        </div>
      </div>

      {/* Example 3: max() */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 3: max()</h2>
        <p>
          The <code>max()</code> function picks the <b>largest value</b> among
          its arguments.
        </p>

        <CodeCopyBlock
          code={`.box {
  width: max(30%, 200px);
}`}
          language="css"
        />

        <div
          style={{
            width: "max(30%, 200px)",
            height: "80px",
            background: "#bbf7d0",
            marginTop: "1rem",
            textAlign: "center",
            lineHeight: "80px",
          }}
        >
          Width = max(30%, 200px)
        </div>
      </div>

      {/* Example 4: clamp() */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 4: clamp()</h2>
        <p>
          The <code>clamp()</code> function defines a value that is{" "}
          <b>clamped</b> between a minimum, preferred, and maximum value. Very
          useful for responsive typography.
        </p>

        <CodeCopyBlock
          code={`.title {
  font-size: clamp(1rem, 2.5vw, 2rem);
}`}
          language="css"
        />

        <h2
          style={{
            fontSize: "clamp(1rem, 2.5vw, 2rem)",
            background: "#fecaca",
            padding: "10px",
            textAlign: "center",
            borderRadius: "6px",
            marginTop: "1rem",
          }}
        >
          Responsive Title (clamp)
        </h2>
      </div>

      {/* Example 5: Combined Usage */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 5: Combining Functions</h2>
        <p>
          Math functions can be <b>nested</b> for advanced layouts.
        </p>

        <CodeCopyBlock
          code={`.container {
  width: min(80%, max(300px, calc(50% + 100px)));
}`}
          language="css"
        />

        <div
          style={{
            width: "min(80%, max(300px, calc(50% + 100px)))",
            height: "80px",
            background: "#fbcfe8",
            marginTop: "1rem",
            textAlign: "center",
            lineHeight: "80px",
          }}
        >
          width: min(80%, max(300px, calc(50% + 100px)))
        </div>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          Practice using <code>calc()</code>, <code>min()</code>,{" "}
          <code>max()</code>, and <code>clamp()</code> to build{" "}
          <b>responsive layouts</b> and <b>fluid typography</b>. They give you
          much more flexibility than fixed values.
        </p>
      </div>
    </div>
  );
};

export default CSS_MathFunctions;
